import { useEffect, useMemo, useState } from "react"

import { useLazyGetCitiesQuery, useLazyGetSuburbsQuery } from "store/api/dictionaryApi"
import { ApiTypes, UITypes } from "types"

import { getAddressLabelsByCountry } from "utils/addressUtils"

import { SelectDynamic } from "../SelectDynamic"
import { SelectStatic } from "../SelectStatic"

interface CitySuburbSelectProps {
  showSuburb: boolean
  zip_code: string
  state_id: string | undefined
  country: string | undefined
  values: UITypes.Option[]
  error?: string
  skipLabel?: boolean
  onChange: (value: UITypes.Option[]) => void
}

const CitySuburbSelect = ({
  showSuburb,
  zip_code,
  state_id,
  country,
  values,
  onChange,
  error,
  skipLabel,
}: CitySuburbSelectProps) => {
  const [searchValue, setSearchValue] = useState("")
  const addressLabels = getAddressLabelsByCountry(country)
  const [triggerGetSuburbs, { data: suburbsData }] = useLazyGetSuburbsQuery()
  const options = useMemo(() => {
    return (
      suburbsData?.data.map((item: ApiTypes.Model.Suburb) => ({
        value: item.id,
        label: item.name,
      })) || []
    )
  }, [suburbsData])

  useEffect(() => {
    if (showSuburb) {
      triggerGetSuburbs({ zip_code })
    }
    setSearchValue("")
  }, [zip_code, showSuburb])

  const handleSuburbSearch = (value: string) => {
    setSearchValue(value)
    triggerGetSuburbs({ name: value })
  }

  useEffect(() => {
    if (suburbsData?.data?.length === 1 && searchValue === "") {
      onChange(options)
    }
  }, [suburbsData])

  if (showSuburb) {
    return (
      <SelectStatic
        label={skipLabel ? "" : addressLabels.city}
        options={options}
        values={values}
        onChange={value => onChange(value)}
        onInputChange={handleSuburbSearch}
        placeholder={`Enter ${addressLabels.city}`}
        disabled={!zip_code}
        errorMessage={error}
      />
    )
  }

  return (
    <SelectDynamic
      key={"city"}
      label={skipLabel ? "" : addressLabels.city}
      valueName="id"
      labelName="name"
      name={"city"}
      useLazyQuery={useLazyGetCitiesQuery}
      placeholder={`Enter ${addressLabels.city}`}
      values={values || []}
      onChange={value => onChange(value)}
      disabled={!zip_code}
      errorMessage={error}
      queryParams={{ zip_code: zip_code || undefined, state_id: state_id || undefined }}
    />
  )
}

export default CitySuburbSelect
